import { throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { CoralResponse } from '../client';
/**
 * Impose a client-side request timeout.
 *
 * The interceptor will use timeout values specified in the request config, if available.
 * In this case, the timeout value provided to the constructor is used as a fallback, if a matching
 * entry could not be determined.
 *
 * The expected format for timeouts in client configuration is:
 *
 * @example
 * {
 *   timeouts: {
 *     BeerService/GetAllBeers: 5000,
 *     BeerService/CreateBeer: 2500,
 *     BeerService/*: 1000 // wildcard
 *   }
 * }
 *
 * If used *before* CoralRetryInterceptor in the interceptor chain, this will
 * set a total timeout for the entire request (i.e. all retries combined). If
 * used after CoralRetryInterceptor it will timeout each individual attempt.
 *
 * @example
 * new InterceptingRequestHandler(
 *   new FetchRequestHandler(),
 *   [
 *     new CoralTimeoutInterceptor(10000), // timeout the entire request
 *     new CoralRetryInterceptor(),
 *     new CoralTimeoutInterceptor(1000), // timeout each retry attempt
 *   ]
 * )
 */
export class CoralTimeoutInterceptor {
    constructor(fallbackTimeoutMs) {
        this.fallbackTimeoutMs = fallbackTimeoutMs;
    }
    intercept(request, next) {
        return next.handle(request).pipe(timeout(this.getTimeoutMs(request)), catchError(error => {
            if (error.name === 'TimeoutError') {
                return throwError(new CoralResponse({
                    ok: false,
                    status: 408,
                    statusText: 'Request Timeout',
                }));
            }
            else {
                return throwError(error);
            }
        }));
    }
    getTimeoutMs(request) {
        const config = request.config;
        if (!config.timeouts || !config.timeouts.operationTimeouts)
            return this.fallbackTimeoutMs;
        // If timeout values are defined in the client config, match by operation name (preferred) or wildcard (*) entry (fallback).
        const operation = request.operation;
        const timeoutByOperation = Object.entries(config.timeouts.operationTimeouts).filter(entry => {
            // Key format: $serviceName/$operationName
            const operationNameWithTimeout = entry[0].split('/')[1];
            return operationNameWithTimeout === '*' || operationNameWithTimeout === operation;
        }).sort(first => {
            // Sort so that timeout values matched by operationName are in the front of the list whereas wildcard entries are in the back.
            return first[0].split('/')[1] === '*' ? 1 : -1;
        });
        // If there is a timeout value for the operation or a wildcard entry exists, the first value in the array should be selected.
        if (timeoutByOperation.length > 0)
            return timeoutByOperation[0][1];
        // If there is no match, return the fallback timeout value.
        return this.fallbackTimeoutMs;
    }
}
